import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSport } from "@/context/SportContext";
import { Save, Wind, Power } from "lucide-react";
import { toast } from "sonner";

export default function FeatureSettings() {
  const { reloadFeatures } = useSport();
  const [features, setFeatures] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/features").then((r) => setFeatures(r.data));
  }, []);

  if (!features) return <div className="label-mono text-[#A3A3A3]">Loading…</div>;

  const toggle = (key) => setFeatures((f) => ({ ...f, [key]: !f[key] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/features", features);
      await reloadFeatures();
      toast.success("Features updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="feature-settings">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">ADMIN · FEATURES</div>
          <h1 className="h-display text-5xl tracking-tighter">Sport &amp; feature flags.</h1>
          <div className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">Rally Lab is pickleball-first. Enable additional sports here when you&apos;re ready to roll them out to the public site and your coaches.</div>
        </div>
        <button onClick={save} disabled={saving} className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-features">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="card-surface p-6 fade-up d1">
        <Toggle
          icon={Wind}
          title="Enable Squash"
          desc="Adds squash as a second sport across the landing page, coach offerings, calendars, dashboards and reports. Existing pickleball data is unaffected. Defaults to off."
          color="#CCFF00"
          active={!!features.squash_enabled}
          onToggle={() => toggle("squash_enabled")}
          testid="toggle-squash"
        />
      </div>

      {/* Iter130/131 — Coach cap + per-category overrides. */}
      <div className="card-surface p-6 fade-up d2 space-y-4" data-testid="max-coaches-card">
        <div>
          <div className="text-white font-semibold mb-1">Max coaches per request</div>
          <div className="text-sm text-[#A3A3A3] max-w-xl leading-relaxed">
            Global default cap. Per-category overrides let you tune Group / Club separately — leave blank to use the global. <span className="text-[#525252]">(Solo is always locked to 1 coach.)</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Global</div>
            <input type="number" min={1} max={8}
              value={features.max_coaches_per_request ?? 2}
              onChange={(e) => setFeatures((f) => ({ ...f, max_coaches_per_request: Math.max(1, Math.min(8, Number(e.target.value) || 2)) }))}
              data-testid="max-coaches-input"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-center text-lg font-mono"
            />
          </label>
          {[
            ["max_coaches_group", "Group", "group"],
            ["max_coaches_club", "Club", "club"],
          ].map(([k, l, testid]) => (
            <label key={k} className="block">
              <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">{l}</div>
              <input type="number" min={1} max={8} placeholder="—"
                value={features[k] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const v = raw === "" ? null : Math.max(1, Math.min(8, Number(raw) || 1));
                  setFeatures((f) => ({ ...f, [k]: v }));
                }}
                data-testid={`max-coaches-${testid}`}
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-center text-lg font-mono"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="card-surface p-6 fade-up d2 text-sm text-[#A3A3A3]">
        <div className="flex items-start gap-3">
          <Power className="w-4 h-4 text-[#FF4500] mt-0.5" />
          <div>
            <div className="text-white font-semibold mb-1">Always-on: Pickleball</div>
            Pickleball is the core product and cannot be disabled. All new student signups and coach offerings default to pickleball.
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({ icon: Icon, title, desc, color, active, onToggle, testid }) {
  return (
    <div className="flex items-start gap-4 justify-between flex-wrap">
      <div className="flex items-start gap-3 flex-1 min-w-[280px]">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color }} strokeWidth={1.5} />
        <div>
          <div className="text-base font-semibold mb-1">{title}</div>
          <div className="text-sm text-[#A3A3A3] max-w-xl leading-relaxed">{desc}</div>
        </div>
      </div>
      <button onClick={onToggle} data-testid={testid}
        className="relative w-16 h-8 transition-colors"
        style={{ background: active ? color : "#262626" }}>
        <span className="absolute top-1 transition-all w-6 h-6"
          style={{ background: active ? "#0A0A0A" : "#A3A3A3", left: active ? "calc(100% - 28px)" : "4px" }} />
      </button>
    </div>
  );
}
